import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import {Experience} from '@wix/ambassador-table-reservations-v1-experience/types'
import {ReservationLocation} from '@wix/ambassador-table-reservations-v1-reservation-location/types'

import {noop} from '../../utils/helpers'
import {EXPERIENCES_LIST_REFERRER} from '../../utils/constants'
import {goToExperienceDetails} from '../../utils/navigation'
import {createStorageProvider} from '../../utils/storageContext'
import {RequestStatus, wrapRequest} from '../../utils/wrapRequest'
import {experiencesService} from '../../services/experiencesService'
import {reservationLocationsService} from '../../services/reservationLocationsService'
import {getRegionalSettings} from '../../utils/flowAPI'
import {getReservationLocationsMock} from '../../editor/editorMocks/getReservationLocationsMock'
import {getExperiencesMock} from '../../editor/editorMocks/getExperiencesMock'

import {
  ExperiencesFilterConfig,
  ExperiencesDisplayMode,
  ExperienceSortOrder,
  ExperienceStatusFilter,
} from './Settings/Tabs/Experiences/settingsParams'

interface ExperiencesAddOnData {
  getReservationLocationsStatus: RequestStatus
  getExperiencesStatus: RequestStatus
  regionalSettings?: string
  reservationLocations: ReservationLocation[]
  experiences: Experience[]
  fitToContentHeight: boolean
  filterConfig: ExperiencesFilterConfig
}

interface ExperiencesAddOnActions {
  getReservationLocations: () => Promise<ReservationLocation[] | undefined>
  getExperiences: () => Promise<Experience[] | undefined>
  goToExperienceDetailsPage: (experience: Experience) => void
}

export interface ExperiencesAddOnStorage extends ExperiencesAddOnData, ExperiencesAddOnActions {}

const defaultFilterConfig: ExperiencesFilterConfig = {
  displayMode: ExperiencesDisplayMode.All,
  sortOrder: ExperienceSortOrder.Alphabetically,
  statusFilter: ExperienceStatusFilter.UpcomingAndPast,
}

const defaultExperiencesAddOnStorage: ExperiencesAddOnStorage = {
  getReservationLocationsStatus: RequestStatus.DEFAULT,
  getExperiencesStatus: RequestStatus.DEFAULT,
  reservationLocations: [],
  experiences: [],
  fitToContentHeight: true,
  filterConfig: defaultFilterConfig,
  getReservationLocations: noop,
  getExperiences: noop,
  goToExperienceDetailsPage: noop,
}

const {withStorageProvider, useStorage} = createStorageProvider(defaultExperiencesAddOnStorage)

const initExperiencesAddOnStorage = (
  flowAPI: ControllerFlowAPI,
  filterConfig: ExperiencesFilterConfig,
): ExperiencesAddOnStorage => {
  return {
    ...defaultExperiencesAddOnStorage,
    regionalSettings: getRegionalSettings(flowAPI),
    filterConfig,
    getReservationLocations: wrapRequest(
      flowAPI,
      reservationLocationsService.getReservationLocations,
      'reservationLocations',
      'getReservationLocationsStatus',
    ),
    getExperiences: wrapRequest(
      flowAPI,
      experiencesService.getExperiences,
      'experiences',
      'getExperiencesStatus',
    ),
    goToExperienceDetailsPage: (experience: Experience) =>
      goToExperienceDetails({flowAPI, experience, referrer: EXPERIENCES_LIST_REFERRER}),
  }
}

const mockExperiencesAddOnStorage = (
  flowAPI: ControllerFlowAPI,
  filterConfig: ExperiencesFilterConfig,
): ExperiencesAddOnStorage => {
  const initiatedStorage = initExperiencesAddOnStorage(flowAPI, filterConfig)

  return {
    ...initiatedStorage,
    getReservationLocations: wrapRequest(
      flowAPI,
      getReservationLocationsMock,
      'reservationLocations',
      'getReservationLocationsStatus',
    ),
    getExperiences: wrapRequest(flowAPI, getExperiencesMock, 'experiences', 'getExperiencesStatus'),
  }
}

export {
  initExperiencesAddOnStorage,
  mockExperiencesAddOnStorage,
  withStorageProvider as withExperiencesAddOnStorageProvider,
  useStorage as useExperiencesAddOnStorage,
}
